import { en, LOCALES, type Locale, type MessageKey } from "./messages";
import { safeStorage } from "../../../../shared/lib/safeStorage";
import { chunkFailureVerdict } from "../features/release/staleBuild";
import { withChunkTimeout } from "../../../../shared/lib/moduleLoadFailure";
import { CHUNK_WAIT_MS } from "../lib/chunkWait";
import type { ServerCopyFields } from "./serverCopy";
import { formatAmount } from "./money";
// The Terms track's Spanish stays IN THE ENTRY (rc.12 (b)): the Terms sentence
// follows the SERVED Terms' language whatever the page's (TermsAgreement, rule
// d; Legal), so it must be there before, and without, the Spanish chunk.
import { es as termsEs } from "./messages/es/terms";

const TERMS_ES: Partial<Record<MessageKey, string>> = termsEs;

export type { Locale, MessageKey };
export { LOCALES };

const STORAGE_KEY = "lura.locale";

function isLocale(value: string | null | undefined): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * Which language to render in.
 *
 * Order: an explicit choice the visitor made, then what their browser asks for,
 * then English. `navigator.languages` is checked in order and matched on the
 * PRIMARY subtag, so `es-MX` and `es-419` both find `es` — matching the full
 * tag would silently drop most Spanish speakers.
 */
export function detectLocale(): Locale {
  // Private browsing and some embedded webviews throw on storage access rather
  // than returning null; safeStorage reads that as null. A language preference
  // is not worth a blank page, so fall through to the browser's.
  const stored = safeStorage.local.get(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  const requested = globalThis.navigator?.languages ?? [];
  for (const tag of requested) {
    const primary = tag.split("-")[0]?.toLowerCase();
    if (isLocale(primary)) return primary;
  }
  return "en";
}

export function setLocale(locale: Locale): void {
  // Same as above — when storage fails the choice just does not persist.
  safeStorage.local.set(STORAGE_KEY, locale);
}

/*
 * THE SPANISH CATALOGUE, LOADED ON DEMAND (rc.12 (b), the Web Director's
 * plan). English is in the entry; Spanish is one chunk
 * (messages/es/spanish.ts) that only Spanish readers fetch. main.tsx starts it
 * at entry evaluation when the locale resolves to Spanish, and the language
 * switch loads it before switching.
 *
 * A failed load asks build-info (`chunkFailureVerdict`, rc.12 (a)): only a
 * newer release live raises the page's one release notice. Otherwise
 * (current, unreachable, offline) there is no notice: the page is, truthfully,
 * English, and says so (lang and the switch show English). Either way the
 * STORED choice stays Spanish, so the next load tries again.
 */
export interface Spanish {
  readonly messages: Partial<Record<MessageKey, string>>;
  readonly reviewCopy: Readonly<Record<string, ServerCopyFields>>;
}
let spanish: Spanish | null = null;
let spanishLoad: Promise<boolean> | null = null;

/** The loaded Spanish catalogue, or null when it is not (yet) here. */
export function loadedSpanish(): Spanish | null {
  return spanish;
}

/** Adopt the Spanish module. The loader below, and test setups, which import it statically. */
export function adoptSpanish(module: { es: Spanish["messages"]; reviewCopyEs: Spanish["reviewCopy"] }): void {
  spanish = { messages: module.es, reviewCopy: module.reviewCopyEs };
}

/** Test seam: back to "not loaded", so a test can drive the loader and the boot. */
export function __forgetSpanishForTests(): void {
  spanish = null;
  spanishLoad = null;
}

/** Load the Spanish chunk once; true when it is here. A failure asks build-info (chunkFailureVerdict). */
export function loadSpanish(): Promise<boolean> {
  if (spanish) return Promise.resolve(true);
  // Bounded (rc.13): a hung request becomes a failure after CHUNK_WAIT_MS:
  // English, and the release notice only if build-info says newer.
  spanishLoad ??= withChunkTimeout(import("./messages/es/spanish"), CHUNK_WAIT_MS).then(
    (module) => {
      adoptSpanish(module);
      return true;
    },
    () => {
      spanishLoad = null;
      // The release notice only if build-info says a newer release is live
      // (rc.12 (a)); otherwise the page is, truthfully, English.
      void chunkFailureVerdict();
      return false;
    }
  );
  return spanishLoad;
}

/*
 * WHAT ELSE A SPANISH READER'S BOOT WAITS FOR (rc.12 (a)). A screen that
 * needs a step chunk (views/Book/lazySteps.ts) holds the boot until that
 * chunk settles too, so a Spanish visitor landing on a lazy step sees ONE
 * skeleton (the boot's), then the step: never the boot skeleton, then the
 * step's own fallback. Only what a screen NEEDS holds the boot; a prefetch
 * does not. Settled either way: a failed chunk is the route's error to show.
 */
const bootHolds = new Set<Promise<void>>();

/** The boot waits for `load` too. Returns `load` itself. */
export function holdBoot<T>(load: Promise<T>): Promise<T> {
  const settled = load.then(
    () => undefined,
    () => undefined
  );
  bootHolds.add(settled);
  void settled.then(() => bootHolds.delete(settled));
  return load;
}

/** Resolves once nothing holds the boot, including holds added meanwhile. */
export async function bootReleased(): Promise<void> {
  while (bootHolds.size > 0) await Promise.all([...bootHolds]);
}

/*
 * WORDS A LAZY CHUNK BRINGS (W6 step 2, the pay-string split; Web Director,
 * 2026-10-20). The pay screens' words (messages/payScreens.ts and its Spanish)
 * are not in the entry: each lazy pay chunk imports payScreenMessages.ts,
 * whose evaluation registers both languages here BEFORE the chunk's first
 * render, so no screen can render without its words. A key no chunk has
 * registered reads as "" in a build (never the raw key, never "undefined"),
 * and THROWS in dev and Vitest (Web Director, 2026-10-20): a blank button or
 * heading is an accessibility failure nobody sees, so it must fail loudly
 * wherever someone is looking.
 */
const chunkEn: Partial<Record<MessageKey, string>> = {};
const chunkEs: Partial<Record<MessageKey, string>> = {};

/** A key with no English anywhere: "" in a build; an error in dev and Vitest. */
function unregistered(key: MessageKey): string {
  if (import.meta.env.DEV) throw new Error(`i18n: "${key}" has no words here. Is its chunk's registration imported (i18n/payScreenMessages.ts)?`);
  return "";
}

/** A lazy chunk's words, both languages, registered at its evaluation. */
export function registerChunkMessages(english: Partial<Record<MessageKey, string>>, spanish: Partial<Record<MessageKey, string>>): void {
  Object.assign(chunkEn, english);
  Object.assign(chunkEs, spanish);
}

/**
 * Look up a message and substitute `{name}` placeholders.
 *
 * Falls back to English per KEY rather than per locale: a partially translated
 * catalogue shows Spanish where it has it and English where it does not, which
 * is better than an all-or-nothing switch. The untranslated count is asserted
 * in scripts/validate-quality-contracts.js so "partial" cannot quietly become
 * "abandoned".
 */
export function translate(
  locale: Locale,
  key: MessageKey,
  values?: Record<string, string | number>
): string {
  return translateWith(spanish, locale, key, values);
}

/**
 * `translate` against a GIVEN Spanish catalogue (null: not loaded). The
 * provider passes the catalogue it holds as state, so its `t` is a new
 * function when Spanish lands: a compiled consumer caches `t(...)` by `t`'s
 * identity, and a `t` that read module state would keep the old text.
 */
export function translateWith(
  catalogue: Spanish | null,
  locale: Locale,
  key: MessageKey,
  values?: Record<string, string | number>
): string {
  // Spanish once its chunk is here, English per key it lacks. BEFORE the chunk
  // a Spanish key is EMPTY, never English: that is a Spanish reader's boot, a
  // tree mounted but not displayed, and an empty string reaches nothing a
  // reader meets (the tab title included: "Lura", or "<business> · Lura").
  // The Terms track's Spanish is in the entry, so it is there throughout.
  // A chunk's registered words (above) after the catalogue's own.
  const english: string = en[key] ?? chunkEn[key] ?? unregistered(key);
  const template = locale === "es"
    ? catalogue?.messages[key] ?? chunkEs[key] ?? TERMS_ES[key] ?? (catalogue ? english : "")
    : english;

  if (!values) return template;

  // Only substitutes placeholders the caller supplied. An unknown `{token}` is
  // left as-is rather than blanked, so a missing value is visible in review
  // instead of rendering a sentence with a hole in it.
  //
  // ONE FULL STOP (rc.14; the rc.13 QE's P3, Web Director): a value that
  // already ends in "." (Spanish times, "10:30 a.m."; a business named
  // "Studio Inc.") takes the place of the template's own sentence-ending
  // period instead of doubling it ("a.m..").
  return template.replace(/\{(\w+)\}(\.?)/g, (match, name: string, stop: string) => {
    if (!(name in values)) return match;
    const value = String(values[name]);
    return value.endsWith(".") ? value : value + stop;
  });
}

/** Locale-correct formatters. A booking flow is mostly these. */
/**
 * An ISO instant this surface can actually render.
 *
 * The selection lives in the QUERY STRING, so `?time=` is one hand-edit or one
 * mangled email link away from being anything at all. `new Date("garbage")` is
 * an Invalid Date, and `Intl.DateTimeFormat().format()` THROWS `RangeError` on
 * one — during render, which takes out the whole route rather than the field.
 */
export function isValidInstant(iso: string | null | undefined): iso is string {
  return typeof iso === "string" && !Number.isNaN(new Date(iso).getTime());
}

/**
 * The `lang` for words the SERVER sent in a locale it names (a cancellation
 * policy): only the two languages Book speaks, else none (security audit
 * HD-2). A missing or unexpected value then inherits the page's language
 * instead of putting server text into an attribute, or throwing on null.
 */
export function servedLang(locale: unknown): "en" | "es" | undefined {
  return locale === "EN" ? "en" : locale === "ES" ? "es" : undefined;
}

/**
 * Format, or say nothing — but never throw.
 *
 * Guards upstream should stop an unparseable instant before it reaches a
 * formatter, and they do. This is the backstop for the case they miss, because
 * the failure mode without it is a blank error page on the review screen rather
 * than a missing time.
 *
 * An em dash rather than an empty string: something visibly absent is a bug
 * someone reports, and a silent gap is a bug someone lives with. The timeZone
 * is guarded by the same try — a bad IANA name throws from the CONSTRUCTOR, not
 * from format, so checking the date alone would not have covered it.
 */
function safeFormat(iso: string, build: () => Intl.DateTimeFormat): string {
  try {
    return build().format(new Date(iso));
  } catch {
    return "—";
  }
}

export function formatters(locale: Locale) {
  const intlLocale = locale === "es" ? "es-US" : locale;
  return {
    /** Cents to currency, by Book's one money rule (./money). The API exposes cents with no currency code; USD until it says otherwise. */
    money: (cents: number) => formatAmount(cents, "USD", locale),

    /**
     * A service length. Not `Intl.DurationFormat` — Safari shipped it late
     * enough that a booking surface aimed at phones cannot rely on it yet.
     */
    duration: (minutes: number) => {
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      const plural = new Intl.PluralRules(intlLocale);
      const hourWord = locale === "es" ? (plural.select(hours) === "one" ? "hora" : "horas") : "hr";
      const minWord = locale === "es" ? "min" : "min";
      if (hours === 0) return `${rest} ${minWord}`;
      if (rest === 0) return `${hours} ${hourWord}`;
      return `${hours} ${hourWord} ${rest} ${minWord}`;
    },

    /** A date in the PROFESSIONAL's timezone — the appointment happens there, not where the client is. */
    date: (iso: string, timeZone: string) =>
      safeFormat(iso, () =>
        new Intl.DateTimeFormat(intlLocale, {
          weekday: "long",
          month: "long",
          day: "numeric",
          timeZone,
        })
      ),

    /** Localized generic zone name, tied to the appointment's own instant/zone. */
    zone: (iso: string, timeZone: string) => {
      try {
        return new Intl.DateTimeFormat(intlLocale, { timeZone, timeZoneName: "longGeneric" })
          .formatToParts(new Date(iso)).find((part) => part.type === "timeZoneName")?.value ?? "—";
      } catch {
        return "—";
      }
    },

    time: (iso: string, timeZone: string) =>
      safeFormat(iso, () =>
        new Intl.DateTimeFormat(intlLocale, { hour: "numeric", minute: "2-digit", timeZone })
      ),
  };
}
